import React, { useState } from 'react';
import { CreditCard, Headphones, Menu, Play, X } from 'lucide-react';

const PUBLIC_HEADER_TAB_STORAGE_KEY = 'publicHeaderTab';

const DEFAULT_TABS = [
  { id: 'home', label: 'Home' },
  { id: 'gallery', label: 'Gallery' },
  { id: 'howitworks', label: 'How it works' },
  { id: 'about', label: 'About Us' },
  { id: 'pricing', label: 'Pricing' },
];

export default function PublicHeader({
  activeTab,
  onTabChange,
  activeViewId,
  setActiveView,
  onLaunchAI,
  onSupportClick,
  tabs = DEFAULT_TABS,
  variant = 'light',
}) {
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const dark = variant === 'dark';

  const navigateToAboutUs = () => {
    setActiveView?.('about-us');
    window.history.pushState({ view: 'about-us' }, '', '/about-us');
  };

  const navigateToFabricTemplates = () => {
    setActiveView?.('fabric-templates');
    window.history.pushState({ view: 'fabric-templates' }, '', '/fabric-templates');
  };

  const changeTab = (id) => {
    if (id === 'about') {
      navigateToAboutUs();
      return;
    }

    if (onTabChange) {
      onTabChange(id);
      return;
    }

    try {
      window.sessionStorage.setItem(PUBLIC_HEADER_TAB_STORAGE_KEY, id);
    } catch (_) {}
    setActiveView?.('home');
    window.history.pushState({ view: 'home' }, '', '/');
  };

  const isTabActive = (id) =>
    activeTab === id || (id === 'about' && activeViewId === 'about-us');

  const launchAI = () => {
    if (onLaunchAI) return onLaunchAI();
    setActiveView?.('login');
  };

  const isAdmin =
    typeof localStorage !== 'undefined' && localStorage.getItem('role')?.toLowerCase() === 'admin';
  const secondaryLinks = [
    { id: 'studio-info', label: 'AI Studio' },
    { id: 'catalogue-info', label: 'Create free catalogue' },
    { id: 'virtualtry-info', label: 'Virtual Try On' },
    { id: 'fabric-templates', label: 'Fabric Templates' },
    ...(isAdmin ? [{ id: 'browse-vendors', label: 'Browse vendors' }] : []),
  ];

  const secondaryLinkClassName = (id) =>
    `text-sm font-medium transition-colors ${
      activeViewId === id
        ? dark
          ? 'text-white font-semibold underline underline-offset-8 decoration-2 decoration-gold'
          : 'text-blueprint-ink font-semibold underline underline-offset-8 decoration-2 decoration-blueprint-clay'
        : dark
          ? 'text-white/60 hover:text-white'
          : 'text-blueprint-gravel hover:text-blueprint-ink'
    }`;

  const tabClass = (active) =>
    active
      ? dark
        ? 'bg-gold text-white'
        : 'bg-blueprint-ink text-white'
      : dark
        ? 'text-white/60 hover:text-white hover:bg-white/10'
        : 'text-blueprint-gravel hover:text-blueprint-ink hover:bg-blueprint-powder';

  return (
    <header className="fixed top-0 left-0 right-0 z-50">
      <div
        className={`absolute inset-0 ${
          dark ? 'bg-noir/75 backdrop-blur-xl border-b border-white/10' : 'ab-public-header'
        }`}
      />
      <div className="container mx-auto px-4 sm:px-6 py-4 relative">
        <div className="flex items-center justify-between gap-2 sm:gap-4">
          <button
            type="button"
            onClick={() => changeTab('home')}
            className="flex items-center gap-3 shrink-0 text-left rounded-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-blueprint-signal/30"
            aria-label="Fashdeck home"
          >
            <div className="fd-brand-mark w-10 h-10 sm:w-11 sm:h-11 rounded-lg">
              <img src="/logo.png?v=20260711" alt="Fashdeck" />
            </div>
            <span
              className={`text-xl sm:text-2xl font-headline font-light tracking-[-0.02em] hidden sm:block ${
                dark ? 'text-white' : 'text-blueprint-ink'
              }`}
            >
              Fashdeck
            </span>
          </button>

          <nav className="hidden sm:flex flex-1 justify-center min-w-0 overflow-x-auto scrollbar-hide" aria-label="Page sections">
            <div
              className={`inline-flex items-center gap-0.5 rounded-full p-1 shrink-0 ${
                dark
                  ? 'border border-white/12 bg-white/5 backdrop-blur'
                  : 'border border-blueprint-chalk bg-white shadow-blueprint-inset'
              }`}
            >
              {tabs.map(({ id, label }) => (
                <button
                  key={id}
                  type="button"
                  onClick={() => changeTab(id)}
                  className={`px-2.5 sm:px-3 md:px-4 py-2 rounded-full text-[11px] sm:text-xs md:text-sm font-medium transition-all whitespace-nowrap ${tabClass(isTabActive(id))}`}
                >
                  {id === 'pricing' ? (
                    <span className="inline-flex items-center gap-1.5">
                      <CreditCard className="w-3.5 h-3.5 opacity-80 shrink-0" aria-hidden />
                      {label}
                    </span>
                  ) : id === 'howitworks' ? (
                    <span className="inline-flex items-center gap-1">
                      <Play className="w-3 h-3 opacity-80 shrink-0" aria-hidden />
                      {label}
                    </span>
                  ) : (
                    label
                  )}
                </button>
              ))}
            </div>
          </nav>

          <div className="flex items-center gap-2 sm:gap-3 shrink-0">
            <nav className="hidden lg:flex items-center gap-5">
              {secondaryLinks.map(({ id, label }) => (
                <button
                  key={id}
                  type="button"
                  onClick={() => {
                    if (id === 'fabric-templates') {
                      navigateToFabricTemplates();
                      return;
                    }
                    setActiveView?.(id);
                  }}
                  className={secondaryLinkClassName(id)}
                >
                  {label}
                </button>
              ))}
            </nav>

            {onSupportClick ? (
              <button
                type="button"
                onClick={onSupportClick}
                className={`relative z-10 inline-flex items-center gap-1.5 rounded-full px-3 py-2 text-xs sm:text-sm font-medium transition-colors shrink-0 ${
                  dark
                    ? 'border border-white/25 bg-white/10 text-white hover:bg-white/15'
                    : 'border border-blueprint-chalk bg-white text-blueprint-ink hover:bg-blueprint-powder'
                }`}
                aria-label="Need support"
              >
                <Headphones className="w-4 h-4 shrink-0" aria-hidden />
                <span className="hidden sm:inline">Support</span>
              </button>
            ) : null}

            <button
              type="button"
              onClick={launchAI}
              className={
                dark
                  ? 'inline-flex items-center justify-center gap-2 rounded-full bg-gradient-to-r from-gold-soft to-gold-deep px-4 sm:px-6 py-2.5 text-xs sm:text-sm font-semibold text-white transition-transform hover:-translate-y-0.5'
                  : 'ab-btn-primary px-4 sm:px-6 py-2.5 text-xs sm:text-sm'
              }
            >
              Sign In
            </button>

            <button
              type="button"
              className={`sm:hidden p-2 rounded-lg ${
                dark
                  ? 'text-white hover:bg-white/10 border border-white/15'
                  : 'text-blueprint-ink hover:bg-blueprint-powder border border-blueprint-chalk'
              }`}
              onClick={() => setShowMobileMenu(true)}
              aria-label="Open menu"
            >
              <Menu className="w-6 h-6" />
            </button>
          </div>
        </div>
      </div>

      {showMobileMenu && (
        <div
          className={`fixed inset-0 z-[60] sm:hidden backdrop-blur-sm ${
            dark ? 'bg-black/60' : 'bg-blueprint-ink/30'
          }`}
          role="dialog"
          aria-modal="true"
          aria-label="Navigation menu"
          onClick={() => setShowMobileMenu(false)}
        >
          <div
            className={`absolute inset-x-0 top-0 max-h-[85vh] overflow-y-auto p-4 ${
              dark
                ? 'bg-[#111113] border-b border-white/10'
                : 'bg-blueprint-vellum border-b border-blueprint-chalk ab-blueprint-grid-fine'
            }`}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-between items-center mb-4">
              <span className={`text-lg font-headline font-light ${dark ? 'text-white' : 'text-blueprint-ink'}`}>Menu</span>
              <button
                type="button"
                onClick={() => setShowMobileMenu(false)}
                className={`p-2 rounded-lg ${
                  dark ? 'hover:bg-white/10 text-white' : 'hover:bg-blueprint-powder text-blueprint-ink'
                }`}
                aria-label="Close menu"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="flex flex-col gap-1">
              {tabs.map(({ id, label }) => (
                <button
                  key={id}
                  type="button"
                  onClick={() => {
                    changeTab(id);
                    setShowMobileMenu(false);
                  }}
                  className={`text-left px-4 py-3 rounded-xl font-medium ${tabClass(isTabActive(id))}`}
                >
                  {label}
                </button>
              ))}

              <hr className={dark ? 'border-white/10 my-2' : 'border-blueprint-chalk my-2'} />

              {secondaryLinks.map(({ id, label }) => (
                <button
                  key={id}
                  type="button"
                  onClick={() => {
                    if (id === 'fabric-templates') {
                      navigateToFabricTemplates();
                    } else {
                      setActiveView?.(id);
                    }
                    setShowMobileMenu(false);
                  }}
                  className={`text-left px-4 py-3 rounded-xl ${
                    activeViewId === id
                      ? dark
                        ? 'bg-gold text-white'
                        : 'bg-blueprint-ink text-white'
                      : dark
                        ? 'text-white/80 hover:bg-white/10'
                        : 'text-blueprint-ink hover:bg-blueprint-powder'
                  }`}
                >
                  {label}
                </button>
              ))}

              {onSupportClick ? (
                <button
                  type="button"
                  onClick={() => {
                    onSupportClick();
                    setShowMobileMenu(false);
                  }}
                  className={`text-left px-4 py-3 rounded-xl font-medium inline-flex items-center gap-2 ${
                    dark ? 'text-white/80 hover:bg-white/10' : 'text-blueprint-ink hover:bg-blueprint-powder'
                  }`}
                >
                  <Headphones className="w-4 h-4" aria-hidden />
                  Support
                </button>
              ) : null}

              <p
                className={`mt-3 px-4 font-mono text-[10px] font-medium uppercase tracking-[0.14em] ${
                  dark ? 'text-gold' : 'text-blueprint-signal'
                }`}
              >
                Spec · AI fashion atelier
              </p>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}

export { PUBLIC_HEADER_TAB_STORAGE_KEY };
